/**
 * SlugRedirect — backward-compat redirect for legacy `/{orgSlug}/...` URLs.
 *
 * Single-domain mode (Feb 2026) drops the slug-prefixed URL pattern. Any
 * bookmark or shared link that still carries an org slug as the first
 * path segment is rewritten in-browser to its slug-less equivalent:
 *
 *   /acme/dashboard          → /dashboard
 *   /acme/courses/abc-123    → /courses/abc-123
 *   /acme/login              → /login
 *
 * We also seed `currentOrgId` from the slug so a SuperAdmin who bookmarked
 * `/acme/login` still lands inside Acme's org context after auth.
 *
 * Mounted in App.js as the LAST catch-all route, so it only triggers when
 * no slug-less route matched.
 */
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import api from '../../api/client';
import { getOrgSlugFromPath, getPathWithoutOrg } from '../../context/OrgContext';

const InlineSpinner = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="slug-redirect-loading">
    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
  </div>
);

export default function SlugRedirect() {
  const location = useLocation();
  const [resolved, setResolved] = useState(false);
  const slug = getOrgSlugFromPath(location.pathname);

  useEffect(() => {
    if (!slug) {
      setResolved(true);
      return;
    }
    let cancelled = false;
    // Try to resolve the slug → orgId so the user lands in the right
    // org context after the redirect. Failure is silent — they'll fall
    // back to their default org from the JWT.
    api.get(`/organizations/resolve?subdomain=${slug}`)
      .then((res) => {
        const org = res?.data;
        if (org?.id && !cancelled) {
          localStorage.setItem('currentOrgId', org.id);
          if (org.subdomain || org.slug) {
            localStorage.setItem('currentOrgSlug', org.subdomain || org.slug);
          }
        }
      })
      .catch(() => { /* unknown slug — just redirect */ })
      .finally(() => { if (!cancelled) setResolved(true); });
    return () => { cancelled = true; };
  }, [slug]);

  // If the first segment isn't a slug (already a slug-less route), this
  // component is being rendered for an actual 404 path. Pass through to
  // /dashboard so the user lands somewhere safe.
  if (!slug) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!resolved) {
    return <InlineSpinner />;
  }
  const target = getPathWithoutOrg(location.pathname, slug) + (location.search || '') + (location.hash || '');
  return <Navigate to={target} replace />;
}
